import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { setLanguage } from '../store/slices/userSlice';
import TcButton from '../components/TcButton';
import TcPaper from '../components/TcPaper';
import { setCurrentExhibit } from '../store/slices/exhibitSlice';
import i18n from '../i18n';
import { useSimpleImagePreloader } from '../hooks/useImagePreloader';
import LoadingScreen from '../components/shared/LoadingScreen';

import r65Character from '../assets/images/r65-character.png';
import qrCode from '../assets/images/tutorial-scan-to-begin.png';

export default function LanguageSelection() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { currentExhibit } = useAppSelector(state => state.exhibit);
    const { language, visitorId } = useAppSelector(state => state.user);
    const [loadingLanguage, setLoadingLanguage] = useState<string | null>(null);

    // Preload all images when component mounts
    const allImages = [
        r65Character,
        qrCode
    ];

    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);

    // Device detection
    const { isDesktop } = useDeviceDetection();
    
    // Sync HTML lang attribute with current i18n language on mount
    useEffect(() => {
        const currentLang = i18n.language;
        
        // If the detected language is Chinese and our Redux state is empty,
        // update Redux state to match i18n
        if (currentLang === 'zh' && language === '') {
            dispatch(setLanguage(currentLang));
        }
        
        // Always ensure HTML lang attribute is synchronized
        document.documentElement.setAttribute('lang', currentLang);
    }, [dispatch, language]);
    
    const languages = [
        { code: 'en', name: 'English' },
        { code: 'zh', name: '中文' },
        // { code: 'ms', name: 'Bahasa Melayu' },
        // { code: 'ta', name: 'தமிழ்' }
    ];
    
    const handleLanguageSelect = async (languageCode: string) => {
        dispatch(setLanguage(languageCode));
        
        // Set loading language state to the specific language code
        setLoadingLanguage(languageCode);
        
        // Clear loading language state
        setLoadingLanguage(null);
        
        navigate('/privacy');
    };

    // Reset the current exhibit to null in state
    if (currentExhibit) dispatch(setCurrentExhibit(null));

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading..." />;
    }
    
    if (isComplete) {
        if (isDesktop) {
            // If the user is on a desktop device, show a full-screen background image with a QR code in the middle
            return (
                <div className="main-content-wrapper bg-image-galaxy !pb-28 unselectable">
                    <div className="flex flex-col items-center justify-center h-full">
                        <TcPaper classNameStyle="max-w-2xl">
                            <h1 
                                className="main-content-title mb-6" 
                                dangerouslySetInnerHTML={{ __html: t('welcome.title') }}
                            />
                            <p className="main-content-subtitle">
                                {t('common.scanQrCodeToBegin')}
                            </p>
                        </TcPaper>
                        <TcPaper classNameStyle="max-w-lg">
                            <img src={qrCode} alt="QR Code" />
                        </TcPaper>
                    </div>
                </div>
            )
        }
        return (
            <div className="main-content-wrapper bg-image-galaxy !pb-28 unselectable">
                <div className={`
                    flex flex-col w-full 2xs:pb-20 xs:pb-0 sm:pb-0 
                    gap-4 2xs:gap-0 xs:gap-0 sm:gap-4 
                    2xs-landscape:gap-0 xs-landscape:gap-0 
                    2xs-landscape:text-left xs-landscape:text-left 
                    2xs-landscape:px-4 xs-landscape:px-12
                `}>
                    <TcPaper classNameStyle="mt-6">
                        <h1 
                            className="main-content-title" 
                            dangerouslySetInnerHTML={{ __html: t('welcome.title') }}
                        />
                    </TcPaper>
                    
                    <TcPaper classNameStyle="flex flex-col gap-6 xs:gap-4 sm:gap-6 2xs-landscape:gap-0 xs-landscape:gap-4 xs-landscape:items-baseline xs-landscape:justify-start">
                        <p className="main-content-subtitle 2xs-landscape:!pb-0 xs-landscape:pb-0 2xs:pb-0 xs:pb-4"
                            dangerouslySetInnerHTML={{ __html: t('welcome.subtitle') }}
                        />
                    
                        <div className={`
                            button-wrapper 
                            gap-4 flex flex-col 2xs-landscape:flex-row xs-landscape:flex-row 
                            2xs-landscape:mx-0 xs-landscape:mx-0 
                            2xs-landscape:pl-0 xs-landscape:pl-0
                        `}>
                            {languages.map(language => (
                                <TcButton
                                    key={language.code}
                                    handleClick={() => handleLanguageSelect(language.code)}
                                    primary
                                    classNameStyle="text-center"
                                    disabled={!!loadingLanguage}
                                >
                                    {loadingLanguage === language.code ? t('common.loading') : language.name}
                                </TcButton>
                            ))}
                        </div>
                    </TcPaper>

                    <div className="fixed bottom-0 left-0 right-0">
                        <div className={`relative mx-auto`}>
                            <img src={r65Character} 
                                alt="R65 Character" 
                                className={`block absolute -bottom-8 2xs-landscape:-bottom-2 xs-landscape:-bottom-2 left-0 right-0 mx-auto 
                                    landscape:left-auto landscape:right-[8%] 
                                    w-auto 
                                    h-[30vh] 
                                    2xs:h-[20vh] 
                                    xs:h-[25vh] 
                                    has-transition `} 
                                />
                        </div>
                    </div>

                </div>
            </div>
        );
    }
} 